// Writes to the three operator-owned RTDB nodes.
//
// Everything here uses update() rather than set(): a note is built up field by
// field across several sittings, and set() would wipe whatever the previous
// sitting recorded.
//
// The rules validate every field individually and reject unknown keys via
// $other, so a typo here does not corrupt data — it fails the write. That is
// the right failure, but it is silent in the browser, which is why the field
// lists below are explicit rather than spread from a form.

import { ref, update } from 'firebase/database'
import { db } from './firebase.js'
import { dealerKey } from './util.js'

const now = () => new Date().toISOString()

/** RTDB rejects undefined outright. null is meaningful — it clears a field. */
const clean = (obj) =>
  Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined))

const NOTE_FIELDS = [
  'status', 'notes', 'tradeValue', 'otdQuote', 'lastContactAt', 'followUpAt',
]

const DEALER_FIELDS = ['salesperson', 'phone', 'email', 'notes']

export const CRITERIA_FIELDS = [
  'yearMin', 'yearMax', 'priceMax', 'mileageMax',
  'mileageSoftMax', 'distanceMaxMiles', 'dealerOnly',
]

const pick = (obj, fields) =>
  clean(Object.fromEntries(fields.map((f) => [f, obj[f]])))

export function saveNote(listingId, patch) {
  return update(ref(db, `/notes/${listingId}`), {
    ...pick(patch, NOTE_FIELDS),
    updatedAt: now(),
  })
}

export function saveDealer(dealerName, patch) {
  const key = dealerKey(dealerName)
  // A dealerless listing has no dealer to store contacts against, and writing
  // them all into a shared 'unknown' bucket would put one seller's phone number
  // on another's bike.
  if (!dealerName || key === 'unknown') {
    return Promise.reject(new Error('Cannot save contacts for a listing with no dealer'))
  }
  return update(ref(db, `/dealers/${key}`), {
    ...pick(patch, DEALER_FIELDS),
    updatedAt: now(),
  })
}

export function saveCriteria(values) {
  const out = {}
  for (const field of CRITERIA_FIELDS) {
    const v = values[field]
    if (v === undefined || v === '') continue
    if (v === null) {
      out[field] = null
      continue
    }
    // Form inputs hand back strings; the rules require numbers and booleans.
    out[field] = field === 'dealerOnly' ? (v === true || v === 'true') : Number(v)
  }
  return update(ref(db, '/criteria'), { ...out, updatedAt: now() })
}

export function markAlertSeen(listingId, at = now()) {
  return update(ref(db, `/notes/${listingId}`), { seenAlertAt: at })
}

export function markAllAlertsSeen(listingIds, at = now()) {
  return Promise.all(listingIds.map((id) => markAlertSeen(id, at))).then(() => undefined)
}
